import type { PatientFile } from '../contexts/types';

function toTimestamp(value: unknown) {
  if (!value) return Date.now();

  if (typeof value === 'number') return value;

  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? Date.now() : parsed;
  }

  if (typeof value === 'object' && value !== null && 'toMillis' in value) {
    const candidate = value as { toMillis: () => number };
    return candidate.toMillis();
  }

  return Date.now();
}

function sanitizeName(value: string) {
  return value
    .trim()
    .replace(/[^\w.-]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function stripExtension(value: string) {
  return value.replace(/\.[^.]+$/, '');
}

function inferContentType(value?: string | null) {
  const normalized = String(value || '').trim().toLowerCase();

  if (!normalized) {
    return 'application/octet-stream';
  }

  if (normalized.includes('pdf')) return 'application/pdf';
  if (normalized.startsWith('image/')) return normalized;
  if (normalized.includes('jpeg')) return 'image/jpeg';
  if (normalized.includes('jpg')) return 'image/jpeg';
  if (normalized.includes('png')) return 'image/png';
  if (normalized.includes('webp')) return 'image/webp';
  if (normalized.includes('gif')) return 'image/gif';
  if (normalized.includes('heic')) return 'image/heic';
  if (normalized.includes('dcm') || normalized.includes('dicom')) return 'application/dicom';

  return normalized;
}

function isGenericMimeType(value?: string | null) {
  const normalized = String(value || '').trim().toLowerCase();

  if (!normalized) return true;
  if (normalized === 'application/octet-stream') return true;
  if (normalized === 'image/legacy') return true;
  if (normalized.startsWith('image/*')) return true;
  if (normalized.startsWith('application/*')) return true;

  return false;
}

function inferMimeFromText(value: string) {
  const normalized = value.toLowerCase();
  if (normalized.includes('.pdf')) return 'application/pdf';
  if (normalized.includes('.png')) return 'image/png';
  if (normalized.includes('.webp')) return 'image/webp';
  if (normalized.includes('.jpeg') || normalized.includes('.jpg')) return 'image/jpeg';
  if (normalized.includes('.dcm') || normalized.includes('.dicom')) return 'application/dicom';
  return '';
}

function resolvePatientFileMimeType(file: Pick<PatientFile, 'contentType' | 'type' | 'name' | 'path' | 'storagePath' | 'url'>) {
  const declaredMime = inferContentType(String(file.contentType || file.type || ''));
  if (!isGenericMimeType(declaredMime)) {
    return declaredMime;
  }

  const mimeFromText = inferMimeFromText(
    [file.name, file.path, file.storagePath, file.url].filter(Boolean).join(' ')
  );

  if (mimeFromText) {
    return mimeFromText;
  }

  if (String(file.contentType || file.type || '').toLowerCase().includes('pdf')) {
    return 'application/pdf';
  }

  if (
    String(file.contentType || file.type || '').toLowerCase().startsWith('image/') ||
    String(file.url || '').toLowerCase().includes('image')
  ) {
    return 'image/jpeg';
  }

  if (String(file.contentType || file.type || '').toLowerCase().includes('dicom') || String(file.name || file.path || file.storagePath || '').toLowerCase().includes('.dcm')) {
    return 'application/dicom';
  }

  return declaredMime;
}

function blobToDataUrl(blob: Blob) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error || new Error('Failed to read blob as data URL.'));
    reader.readAsDataURL(blob);
  });
}

function loadImageElement(source: string) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Não foi possível carregar este arquivo. Tente reenviar ou selecione outro.'));
    image.src = source;
  });
}

async function imageUrlToDataUrl(source: string, mimeType: string) {
  const image = await loadImageElement(source);
  const canvas = document.createElement('canvas');
  canvas.width = image.naturalWidth || image.width || 1;
  canvas.height = image.naturalHeight || image.height || 1;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Não foi possível carregar este arquivo. Tente reenviar ou selecione outro.');
  }

  ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

  try {
    return canvas.toDataURL(mimeType.startsWith('image/') ? mimeType : 'image/png');
  } catch (error) {
    throw new Error('Não foi possível carregar este arquivo. Tente reenviar ou selecione outro.');
  }
}

function buildFileLabel(name: string, contentType?: string | null) {
  const trimmed = name.trim();
  if (trimmed) {
    return trimmed;
  }

  const normalized = String(contentType || '').toLowerCase();
  if (normalized.includes('pdf')) return 'Exame PDF';
  if (normalized.startsWith('image/')) return 'Imagem';

  return 'Exame';
}

function inferPatientFileKind(contentType: string): 'image' | 'pdf' | 'dicom' | 'other' {
  const normalized = String(contentType || '').toLowerCase();

  if (normalized.startsWith('image/')) return 'image';
  if (normalized.includes('pdf')) return 'pdf';
  if (normalized.includes('dcm') || normalized.includes('dicom')) return 'dicom';

  return 'other';
}

function getDedupKey(file: Pick<PatientFile, 'id' | 'storagePath' | 'url' | 'name'>) {
  return [
    String(file.id || '').trim(),
    String(file.storagePath || '').trim(),
    String(file.url || '').trim(),
    String(file.name || '').trim(),
  ].join('|');
}

export function getPatientFileKey(
  file: Pick<PatientFile, 'id' | 'storagePath' | 'url' | 'name' | 'type'>,
  index = 0,
) {
  const base = file.storagePath || file.url || file.id || file.name || `item-${index}`;
  return `${file.type || 'item'}-${base}-${index}`;
}

export function isImagePatientFile(file: Pick<PatientFile, 'contentType' | 'type'>) {
  const value = String(file.contentType || file.type || '').trim().toLowerCase();
  return value.startsWith('image/') || value === 'image/legacy';
}

export function isPdfPatientFile(file: Pick<PatientFile, 'contentType' | 'type'>) {
  const value = String(file.contentType || file.type || '').trim().toLowerCase();
  return value.includes('pdf');
}

export function isDicomPatientFile(file: Pick<PatientFile, 'contentType' | 'type' | 'name' | 'path' | 'storagePath'>) {
  const value = String(file.contentType || file.type || '').trim().toLowerCase();
  if (value.includes('dicom') || value.includes('dcm')) return true;
  const text = [file.name, file.path, file.storagePath].filter(Boolean).join(' ').toLowerCase();
  return text.includes('.dcm') || text.includes('.dicom');
}

export async function patientFileToDataUrl(
  file: Pick<PatientFile, 'url' | 'contentType' | 'type' | 'name' | 'path' | 'storagePath'>
) {
  const source = String(file.url || '').trim();

  if (!source) {
    throw new Error('Arquivo do paciente sem URL.');
  }

  if (source.startsWith('data:')) {
    return source;
  }

  const mime = resolvePatientFileMimeType(file);
  if (mime.startsWith('image/')) {
    return imageUrlToDataUrl(source, mime);
  }

  try {
    const response = await fetch(source);
    if (!response.ok) {
      throw new Error(`Falha ao carregar o arquivo do paciente (${response.status}).`);
    }

    const blob = await response.blob();
    const safeBlob = mime && blob.type !== mime ? blob.slice(0, blob.size, mime) : blob;

    return blobToDataUrl(safeBlob);
  } catch (error) {
    console.error('[PatientFilePickerModal][file_fetch_failed]', error);
    throw new Error('Não foi possível carregar este arquivo. Tente reenviar ou selecione outro.');
  }
}

export function normalizePatientFile(raw: unknown, index = 0): PatientFile | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const data = raw as Record<string, unknown>;
  const url = String(data.url || '').trim();

  if (!url) {
    return null;
  }

  const storagePath = String(data.storagePath || data.path || '').trim();
  const rawName = String(data.name || '').trim();
  const contentType = inferContentType(String(data.contentType || data.type || ''));
  const createdAt = new Date(toTimestamp(data.createdAt)).toISOString();
  const name = buildFileLabel(rawName, contentType);

  return {
    id: String(data.id || storagePath || url || `${sanitizeName(name) || 'item'}-${index}-${createdAt}`),
    name,
    fileName: String(data.fileName || rawName || '').trim() || undefined,
    url,
    storagePath: storagePath || undefined,
    path: storagePath || undefined,
    contentType,
    mimeType: contentType,
    kind: inferPatientFileKind(contentType),
    type: contentType,
    source: data.source === 'web' || data.source === 'mobile' ? data.source : undefined,
    uploadedBy: String(data.uploadedBy || '').trim() || undefined,
    size: typeof data.size === 'number' ? data.size : undefined,
    createdAt,
  };
}

function createLegacyImageFile(url: string, index: number, fallbackCreatedAt: number): PatientFile {
  return {
    id: `legacy-image-${index}-${fallbackCreatedAt}`,
    name: `Imagem ${index + 1}`,
    url,
    storagePath: '',
    path: url,
    contentType: 'image/*',
    type: 'image/legacy',
    createdAt: new Date(fallbackCreatedAt).toISOString(),
  };
}

export function normalizePatientFiles(
  rawPatientFiles: unknown,
  legacyImages?: unknown,
  fallbackCreatedAt?: number,
) {
  const files = Array.isArray(rawPatientFiles)
    ? rawPatientFiles
        .map((item, index) => normalizePatientFile(item, index))
        .filter((item): item is PatientFile => Boolean(item))
    : [];

  const legacyFiles = Array.isArray(legacyImages)
    ? legacyImages
        .filter((item): item is string => typeof item === 'string' && Boolean(item.trim()))
        .map((url, index) => createLegacyImageFile(url.trim(), index, fallbackCreatedAt ?? Date.now()))
    : [];

  const seen = new Set<string>();
  return [...files, ...legacyFiles].filter((file) => {
    const key = getDedupKey(file);
    if (seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
}
